<script setup lang="ts">
import { onMounted } from 'vue';
import { Download, Heart, HeartOff, ShoppingCart } from 'lucide-vue-next';
import FileFeedback from '@/components/FileFeedback.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import useDownloads from '@/composables/useDownloads.ts';
import useFavorites from '@/composables/useFavorites.ts';
import useRecentlyViewed from '@/composables/useRecentlyViewed.ts';
import type { SheetProps } from '@/types';
import type { File } from '@/types/api';

const props = defineProps<
    SheetProps & {
        file: File;
    }
>();

const { isFavorite, addDocument, removeDocument } = useFavorites();
const { trackView } = useRecentlyViewed();
const { trackDownload } = useDownloads();

onMounted(() => {
    trackView(props.file.uid, 'file');
});
</script>

<template>
    <div class="flex flex-col gap-y-6 md:min-w-md">
        <img
            v-if="!file.isEmbeddable && file.preview"
            :src="file.preview.src"
            :srcset="file.preview.srcset"
            class="overflow-hidden rounded-md"
            alt="Dateivorschau"
        />
        <iframe
            v-else-if="file.isEmbeddable"
            :src="`${file.url}#view=fith&toolbar=0&navpanes=0`"
            class="aspect-square w-full lg:aspect-210/295"
        >
        </iframe>
        <CardHeader class="px-0">
            <Badge
                v-if="file.isNew"
                variant="secondary"
                class="mb-1"
                >Neu</Badge
            >
            <CardTitle>{{ file.title }}</CardTitle>
            <CardDescription v-if="file.subtitle">{{
                file.subtitle
            }}</CardDescription>
        </CardHeader>

        <ButtonGroup>
            <ButtonGroup>
                <Button
                    variant="outline"
                    as-child
                >
                    <a
                        :href="file.url"
                        download
                        @click="trackDownload(file.uid)"
                    >
                        <Download />
                        Herunterladen
                    </a>
                </Button>
                <Button
                    variant="outline"
                    v-if="isFavorite(file.uid)"
                    @click="() => removeDocument(file.uid, false)"
                >
                    <HeartOff />
                    Nicht mehr merken
                </Button>
                <Button
                    variant="outline"
                    v-else
                    @click="() => addDocument(file.uid)"
                >
                    <Heart />
                    Als Favorit merken
                </Button>
                <Button
                    v-if="file.shopUrl"
                    variant="outline"
                    as-child
                >
                    <a
                        :href="file.shopUrl"
                        target="_blank"
                    >
                        <ShoppingCart />
                        Bestellen
                    </a>
                </Button>
            </ButtonGroup>
            <FileFeedback :file="file" />
        </ButtonGroup>

        <dl
            class="p-small text-muted-foreground divide-y *:grid *:grid-cols-2 *:gap-2 *:py-1 [&_dd]:text-right"
        >
            <div>
                <dt>Aktualisiert am:</dt>
                <dd>{{ file.dateUpdated }}</dd>
            </div>
            <div>
                <dt>Einstellungsdatum:</dt>
                <dd>{{ file.dateCreated }}</dd>
            </div>
            <div>
                <dt>Dateityp</dt>
                <dd class="uppercase">{{ file.extension }}</dd>
            </div>
            <div>
                <dt>Dateigröße</dt>
                <dd>{{ file.size }}</dd>
            </div>
            <div>
                <dt>Dokumententyp</dt>
                <dd>
                    <Badge variant="outline">{{ file.documentType }}</Badge>
                </dd>
            </div>
            <div v-if="file.targetGroups.length > 0">
                <dt>Zielgruppe</dt>
                <dd class="flex flex-wrap justify-end gap-1">
                    <Badge
                        v-for="targetGroup in file.targetGroups"
                        variant="outline"
                    >
                        {{ targetGroup }}
                    </Badge>
                </dd>
            </div>
        </dl>
    </div>
</template>
